import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { fetchContent, framing, has, lines, paragraphs, text, type ContentMap, publicHref } from '../api/content'
import StudioImage from './StudioImage'

/**
 * About the studio (M13).
 *
 * Everything here is the studio's own wording, carried across from their
 * existing site rather than rewritten. Tonya approved that copy once already;
 * replacing it with something a developer preferred is a decision nobody asked
 * for, and she can edit any of it without a deploy (A-16, D-32).
 */
export default function AboutView() {
  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })

  // The key travels with the URL, because framing is looked up by key and
  // mapping a URL back to one afterwards is a join waiting to go wrong.
  const photos = [1, 2, 3]
    .map((n) => ({ key: `about.photo${n}`, url: text(content, `about.photo${n}`) }))
    .filter((p) => p.url)

  /*
   * The keys travel with the person, for the same reason the photo key travels
   * with the URL above (D-199, D-240).
   *
   * `owners[0]` is whoever *has a name*, not whoever is first — so reading the
   * biography or the framing from a hardcoded `about.owner1.*` puts one
   * person's words under another person's name the moment the first slot is
   * blank, which is a one-field edit on the screen this change exists to make
   * safe.
   */
  const owners = [1, 2]
    .map((n) => ({
      name: text(content, `about.owner${n}.name`),
      role: text(content, `about.owner${n}.role`),
      bio: text(content, `about.owner${n}.bio`),
      bioKey: `about.owner${n}.bio`,
      photo: text(content, `about.owner${n}.photo`),
      photoKey: `about.owner${n}.photo`,
    }))
    .filter((o) => o.name)

  return (
    <div>
      <section className="border-b border-line bg-accent-soft">
        <div className="mx-auto w-full max-w-[52rem] px-5 py-12 text-center">
          <h1 className="font-display text-3xl text-balance">About the studio</h1>

          {has(content, 'about.mission') && (
            <p className="mt-3 font-display text-lg text-accent-2 text-balance">
              {text(content, 'about.mission')}
            </p>
          )}

          {/*
            The three opening paragraphs, centred and up top — the shape the
            studio's own page uses (D-202). They were previously stacked beside
            the owner's portrait, which read as a caption to a photograph rather
            than as what the studio is, and buried the ages and disciplines a
            visitor is scanning for.
          */}
          <div className="mx-auto mt-5 flex w-full max-w-[42rem] flex-col gap-3">
            {['about.story', 'about.offering', 'about.competitive']
              .filter((key) => has(content, key))
              .map((key) => (
                <p key={key} className="text-[15.5px] leading-relaxed text-ink-2 text-balance">
                  {text(content, key)}
                </p>
              ))}
          </div>

          {photos.length > 0 && (
            <div className="mt-8 grid gap-3 sm:grid-cols-3">
              {photos.map((photo) => (
                <StudioImage
                  key={photo.key}
                  src={photo.url}
                  alt=""
                  framing={framing(content, photo.key)}
                  className="aspect-[3/4] w-full rounded-2xl border border-line"
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/*
        Portrait and studio story together, the way the current site has them.
        A photograph of the person who runs the place, next to the paragraph
        explaining what the place is, does more than either alone — and it is
        the arrangement Tonya already chose.
      */}
      {/*
        Nobody named, no section (D-240). A studio that has not filled this in
        gets a page running from the hero straight into the practical sections,
        rather than an empty band where a person ought to be.
      */}
      {owners.length > 0 && (
      <section className="mx-auto w-full max-w-[52rem] px-5 py-12">
        {/*
          Two columns only when there is something to put in the second one
          (D-240). With the biography seeded empty this grid was a 288px
          portrait beside a ~472px column holding one short sentence, and
          `items-start` left roughly 400px of nothing under it — the defect that
          prompted this whole change. It is a ≥640px defect: below `sm:` the
          grid is a single stacked column and was always correct.
        */}
        <div
          className={
            owners[0]?.bio
              ? 'grid items-start gap-8 sm:grid-cols-[minmax(0,18rem)_1fr]'
              : 'grid gap-8'
          }
        >
          {owners[0] && (
            <figure
              className={`w-full max-w-[18rem] mx-auto${owners[0].bio ? ' sm:mx-0' : ''}`}
            >
              {owners[0].photo ? (
                <StudioImage
                  src={owners[0].photo}
                  alt={owners[0].name}
                  framing={framing(content, owners[0].photoKey)}
                  className="aspect-[3/4] w-full rounded-2xl border border-line"
                />
              ) : (
                <span className="grid aspect-[3/4] w-full place-items-center rounded-2xl bg-accent-soft font-display text-4xl font-bold text-accent-2">
                  {owners[0].name
                    .split(/\s+/)
                    .map((w) => w[0])
                    .join('')
                    .slice(0, 2)
                    .toUpperCase()}
                </span>
              )}

              <figcaption
                className={`mt-3 text-center${owners[0].bio ? ' sm:text-left' : ''}`}
              >
                <span className="block text-[15px] font-semibold">{owners[0].name}</span>
                {owners[0].role && (
                  <span className="block text-[13px] text-ink-3">{owners[0].role}</span>
                )}
              </figcaption>
            </figure>
          )}

          {/*
            The owner's own words only. The studio's three paragraphs moved up
            to the opening (D-202) — beside a portrait they read as a caption
            about a person rather than as what the studio is.

            **No fallback sentence.** There used to be one, and it was a
            user-facing literal on a live page standing in for a field seeded
            empty — so it survived Tonya editing every field on the screen,
            because there was no field for it (A-16, D-240). Nothing is the
            honest answer to an unwritten biography.
          */}
          {owners[0]?.bio && (
            <div className="flex min-w-0 flex-col gap-3">
              {paragraphs(content, owners[0].bioKey).map((paragraph, i) => (
                <p key={i} className="text-[15.5px] leading-relaxed text-ink-2">
                  {paragraph}
                </p>
              ))}
            </div>
          )}
        </div>

        {/*
          **Anyone else the studio names, at the same size as the first one**
          (D-453).

          Reported by the owner against her own page: *"The second picture needs
          to be side by side with Tonya and the same size."* The second owner
          rendered as an 80px thumbnail beside her name while the first got a
          288px portrait — so the layout said one of them mattered more, on a
          page about two people who own the studio together.

          The cause was position, not intent: `owners[0]` had a whole branch of
          its own and everybody after it fell into a compact list built for
          "anyone else the studio wants to name". Two people co-owning is not
          that case.

          Same portrait treatment, same aspect ratio, same maximum width, in a
          grid that puts them beside each other from `sm:` up. Below that
          everything stacks, which was always right.
        */}
        {/*
          <b>One column, because this can only ever hold one person</b>
          (D-490).

          It was `sm:grid-cols-2`, from a version of this that imagined a list
          of "anyone else the studio names". `owners` is `[1, 2].map(...)`, so
          `slice(1)` is at most one — the second column could never be filled,
          and its only effect was to pin the second owner to the left half of
          the page while the first one centred above her.
        */}
        {owners.length > 1 && (
          <div className="mt-10 grid gap-8 border-t border-line pt-8">
            {owners.slice(1).map((person) => (
              <figure
                key={person.name}
                /*
                  <b>Centred when there is nothing beside her</b>, exactly as
                  the first owner is (D-490).

                  `asked`: *"when there are two owners on the public site, and
                  no text next to them, they should prob both be centered."*
                  `observed` at 1280: the first owner sat dead centre and the
                  second 252px to the left of it, with a left-aligned caption
                  against a centred one. D-453 had already made them the same
                  *size*; this makes them the same *treatment*.

                  With a biography she stays left, because that is where prose
                  reads from.
                */
                className={`w-full max-w-[18rem]${person.bio ? '' : ' mx-auto'}`}
              >
                {person.photo ? (
                  <StudioImage
                    src={person.photo}
                    alt={person.name}
                    framing={framing(content, person.photoKey)}
                    className="aspect-[3/4] w-full rounded-2xl border border-line"
                  />
                ) : (
                  /*
                    The same initials placeholder the first owner gets. Without
                    it a photoless second owner is a bare name where the other
                    is a portrait, which is the same imbalance one step along.
                  */
                  <span className="grid aspect-[3/4] w-full place-items-center rounded-2xl bg-accent-soft font-display text-4xl font-bold text-accent-2">
                    {person.name
                      .split(/\s+/)
                      .map((w) => w[0])
                      .join('')
                      .slice(0, 2)
                      .toUpperCase()}
                  </span>
                )}

                <figcaption className={`mt-3${person.bio ? '' : ' text-center'}`}>
                  <span className="block text-[15px] font-semibold">{person.name}</span>
                  {person.role && (
                    <span className="block text-[13px] text-ink-3">{person.role}</span>
                  )}
                  {person.bio && (
                    <span className="mt-1.5 flex flex-col gap-2">
                      {paragraphs(content, person.bioKey).map((paragraph, i) => (
                        <span key={i} className="text-[13.5px] leading-relaxed text-ink-2">
                          {paragraph}
                        </span>
                      ))}
                    </span>
                  )}
                </figcaption>
              </figure>
            ))}
          </div>
        )}
      </section>
      )}

      <NeedToKnow content={content} />

      <section className="mx-auto w-full max-w-[44rem] px-5 py-12 text-center">
        <Link
          to={publicHref('/classes')}
          className="rounded-lg bg-accent px-5 py-2.5 text-[14px] font-semibold text-on-accent no-underline hover:brightness-110"
        >
          See this season&rsquo;s classes
        </Link>
      </section>
    </div>
  )
}

/**
 * "Need to know" — the studio's own four sections, in their own shape (D-202).
 *
 * <b>Carried across rather than restructured.</b> The first pass turned four
 * bulleted lists into prose paragraphs and dropped a section entirely, which is
 * how a page with all the right information became hard to follow: eight
 * separate attire rules, one per kind of class, do not survive being run
 * together into a sentence.
 *
 * Kept on the About page rather than given its own, because these are the
 * questions people ask *while* deciding — putting them behind another click
 * means they get asked by phone instead, which is Tonya's evening.
 */
function NeedToKnow({ content }: { content: ContentMap | undefined }) {
  const costs = [
    { label: 'Costume deposit', key: 'needToKnow.costumeDeposit' },
    { label: 'Recital fee', key: 'needToKnow.recitalFee' },
    { label: 'Costume balance', key: 'needToKnow.costumeBalance' },
  ].filter((c) => has(content, c.key))

  const attire = lines(content, 'needToKnow.attire')
  const classes = lines(content, 'needToKnow.classDescriptions')
  const closures = lines(content, 'needToKnow.closures')

  if (costs.length === 0 && attire.length === 0 && classes.length === 0 && closures.length === 0) {
    return null
  }

  return (
    <section className="border-t border-line bg-surface-2">
      <div className="mx-auto w-full max-w-[56rem] px-5 py-12">
        <h2 className="text-center font-display text-2xl">Need to know</h2>

        {has(content, 'needToKnow.intro') && (
          <p className="mx-auto mt-2 w-full max-w-[38rem] text-center text-[13.5px] text-ink-3">
            {text(content, 'needToKnow.intro')}
          </p>
        )}

        {/* Two columns on desktop, the way the studio's own page reads. */}
        <div className="mt-8 grid gap-8 sm:grid-cols-2">
          <Points heading="Required attire" points={attire} />
          <Points heading="Recreational class descriptions" points={classes} />

          {costs.length > 0 && (
            <div>
              <h3 className="font-display text-lg">Recital information</h3>
              <ul className="mt-2.5 flex flex-col gap-1.5">
                {costs.map((cost) => (
                  <li key={cost.key} className="flex gap-2 text-[14px] leading-relaxed text-ink-2">
                    <span aria-hidden="true" className="mt-[0.45rem] size-1.5 shrink-0 rounded-full bg-accent" />
                    <span>
                      <strong className="font-semibold text-ink">{cost.label}</strong>{' '}
                      {text(content, cost.key)}
                    </span>
                  </li>
                ))}
              </ul>

              {/*
                Said once, plainly. Tuition is billed through the studio's
                existing system and is not quoted here — a price on a web page
                that disagrees with an invoice is worse than no price at all.

                Hers, so it is a block (D-240). How the studio bills is exactly
                the kind of sentence that changes without a developer. The
                headings and the three cost labels beside it stay literals —
                making those blocks would file two fields called *Costume
                deposit* next to each other, which is the defect this change
                exists to remove.
              */}
              {has(content, 'needToKnow.tuitionNote') && (
                <p className="mt-2.5 text-[12.5px] text-ink-3">
                  {text(content, 'needToKnow.tuitionNote')}
                </p>
              )}
            </div>
          )}

          <Points heading="Inclement weather and sickness" points={closures} />
        </div>
      </div>
    </section>
  )
}

/** One bulleted section, hidden when the studio has not filled it in. */
function Points({ heading, points }: { heading: string; points: string[] }) {
  if (points.length === 0) return null

  return (
    <div>
      <h3 className="font-display text-lg">{heading}</h3>
      <ul className="mt-2.5 flex flex-col gap-1.5">
        {points.map((point) => (
          <li key={point} className="flex gap-2 text-[14px] leading-relaxed text-ink-2">
            {/* A drawn dot rather than a list marker, so the accent carries the
                studio's colour and wrapped lines align under the text (D-201). */}
            <span aria-hidden="true" className="mt-[0.45rem] size-1.5 shrink-0 rounded-full bg-accent" />
            <span>{point}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
